<template>
  <v-app>
    <loading-layer :value="authorizationProgress" :info="authorizationInfo" />

    <component :is="layout">
      <router-view></router-view>
    </component>
  </v-app>
</template>

<script>
import loadingLayer from '@/components/misc/loading-layer';
import defaultLayout from '@/layouts/Default';
import printLayout from '@/layouts/Print';

import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'App',

  components: {
    loadingLayer,
    defaultLayout,
    printLayout
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentOrganization: 'currentOrganization',
      authorizationProgress: 'authorizationProgress',
      authorizationInfo: 'authorizationInfo',
      languageKey: 'languageKey'
    }),

    layout() {
      return (this.$route.meta.layout || 'default') + 'Layout';
    }
  },

  async beforeMount() {
    if (
      !/callback/.test(window.location.href) &&
      !/silent-renew/.test(window.location.href) &&
      !/error-403/.test(window.location.href)
    ) {
      await this.initializeSession();
    }
    this.$organizationHub.$on('workflows-modified', this.getWorkflows);
    this.$organizationHub.$on(
      'dashboard-processes-updated',
      this.updateDashboardProcesses
    );
    this.$usersHub.$on('dashboard-dates-updated', this.updateDashboardDates);
    this.$usersHub.$on('dashboard-tasks-updated', this.updateDashboardTasks);
  },

  beforeDestroy() {
    this.$organizationHub.leaveOrganization(this.currentOrganization.id);
    this.$usersHub.leaveUserGroup(this.currentUser.id);
    this.$organizationHub.$off('workflows-modified');
    this.$usersHub.$off('dashboard-processes-updated');
    this.$usersHub.$off('dashboard-dates-updated');
    this.$usersHub.$off('dashboard-tasks-updated');
  },

  watch: {
    async currentOrganization(newOrganization, oldOrganization) {
      this.$i18n.locale = this.languageKey.substring(
        0,
        this.languageKey.indexOf('-')
      );

      if (oldOrganization) {
        this.$organizationHub.leaveOrganization(oldOrganization.id);
      }
      if (newOrganization) {
        this.$organizationHub.joinOrganization(newOrganization.id);
        const vm = this;

        // inject custom css
        const customCssResult =  await this.getThemeCss(newOrganization.slug);
        
        const styleTag = document.createElement('style');
        styleTag.id = 'theme-css';
        styleTag.innerHTML = customCssResult.customCss;
        document.head.appendChild(styleTag);

        this.getThemeColors(newOrganization.slug).then(function (
          themeSettings
        ) {
          if (themeSettings !== null) {
            vm.$vuetify.theme.themes.light.primary =
              themeSettings.light.primary;
            vm.$vuetify.theme.themes.light.secondary =
              themeSettings.light.secondary;
            vm.$vuetify.theme.themes.light.accent = themeSettings.light.accent;
            vm.$vuetify.theme.themes.light.background =
              themeSettings.light.background;
            vm.$vuetify.theme.themes.dark.primary = themeSettings.dark.primary;
            vm.$vuetify.theme.themes.dark.secondary =
              themeSettings.dark.secondary;
            vm.$vuetify.theme.themes.dark.accent = themeSettings.dark.accent;
          }
        });
        document.title = `${newOrganization.slug.toUpperCase()} - Development & Performance Tool`;
      } else {
        document.title = `hidden professionals - Development & Performance Tool`;
      }
    },

    currentUser(newUser, oldUser) {
      if (oldUser) {
        this.$usersHub.leaveUserGroup(oldUser?.id);
      }
      if (newUser) {
        this.$usersHub.joinUserGroup(newUser.id);
      }
    }
  },

  methods: {
    ...mapActions({
      initializeSession: 'initializeSession',
      getWorkflows: 'fetchWorkflowSettings',
      updateDashboardProcesses: 'dashboard/updateProcesses',
      updateDashboardDates: 'dashboard/updateDates',
      updateDashboardTasks: 'dashboard/updateTasks',
      getThemeColors: 'getThemeColors',
      getThemeCss: 'getThemeCss'
    })
  }
};

import('@/assets/styles/imports.scss');
</script>

<style>
.theme--light.v-application {
  background-color: var(--v-background-base, white) !important;
}
.v-toolbar__content {
  box-shadow: 0px 2px 1px -1px rgb(0 0 0 / 20%),
    0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%) !important;
}
</style>
